import { useState } from 'react';
import { type MessageKey, useI18n } from '../i18n';
import { Link, useTitle } from '../router';
import { PHET_ATTRIBUTION, PHET_ENABLED, PHET_SIMS, PHET_TOPICS, type PhetTopic, phetPage, phetTitle, phetUrl } from '../lib/phet';
import { NotFoundPage } from './InfoPages';

const OURS: Array<{ id: string; glyph: string }> = [
  { id: 'titration', glyph: 'pH' },
  { id: 'concentration', glyph: 'mol' },
  { id: 'dilution', glyph: 'C₁V₁' },
  { id: 'balancer', glyph: '⇌' },
  { id: 'geometry', glyph: 'AX₄' },
  { id: 'atom', glyph: 'p⁺' },
  { id: 'gas', glyph: 'PV' },
];

export function SimulationsPage() {
  const { t, lang } = useI18n();
  useTitle(t('sims.title'));
  const [topic, setTopic] = useState<PhetTopic | 'all'>('all');
  const list = PHET_SIMS.filter((s) => topic === 'all' || s.topic === topic);

  return (
    <div className="page">
      <header className="page-head">
        <h1>{t('sims.title')}</h1>
        <p className="lead">{t('sims.lead')}</p>
      </header>

      <h2 className="section-title">{t('sims.ours')}</h2>
      <p className="muted">{t('sims.oursLead')}</p>
      <ul className="module-grid sim-cards">
        {OURS.map((s) => (
          <li key={s.id}>
            <Link to={`/lab/${s.id}`} className="module-card">
              <span className="module-glyph" aria-hidden="true">{s.glyph}</span>
              <span className="module-body"><span className="module-title">{t(`lab.sim.${s.id}` as MessageKey)}</span></span>
            </Link>
          </li>
        ))}
      </ul>

      {PHET_ENABLED && (
        <>
          <h2 className="section-title">{t('sims.phet')}</h2>
          <p className="muted">{t('sims.phetLead')}</p>
          <div className="examples" role="group" aria-label={t('sims.phet')}>
            {(['all', ...PHET_TOPICS] as const).map((tp) => (
              <button key={tp} type="button" className={tp === topic ? 'chip-btn on' : 'chip-btn'} aria-pressed={tp === topic} onClick={() => setTopic(tp)}>
                {tp === 'all' ? t('sims.all') : t(`sims.topic.${tp}` as MessageKey)}
              </button>
            ))}
          </div>
          <ul className="module-grid sim-cards phet-grid">
            {list.map((s) => (
              <li key={s.slug}>
                <Link to={`/simulations/phet/${s.slug}`} className="module-card phet-card">
                  <span className="module-body">
                    <span className="module-title">{phetTitle(s, lang)}</span>
                    <span className="module-desc">{t(`sims.topic.${s.topic}` as MessageKey)}</span>
                  </span>
                  <span className="phet-tag" aria-hidden="true">PhET</span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="muted small">{t('sims.licence')} {PHET_ATTRIBUTION}</p>
        </>
      )}
    </div>
  );
}

export function PhetSimPage({ slug }: { slug: string }) {
  const { t, lang } = useI18n();
  const sim = PHET_SIMS.find((s) => s.slug === slug);
  useTitle(sim ? phetTitle(sim, lang) : t('sims.title'));
  if (!PHET_ENABLED || !sim) return <NotFoundPage />;
  return (
    <div className="page">
      <p><Link to="/simulations">← {t('sims.back')}</Link></p>
      <h1>{phetTitle(sim, lang)}</h1>
      <p className="hint">{t('sims.needsInternet')}</p>
      <div className="phet-frame">
        <iframe
          src={phetUrl(sim.slug, lang)}
          title={`${phetTitle(sim, lang)} — PhET Interactive Simulations`}
          allow="fullscreen"
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      </div>
      <p className="attribution">{PHET_ATTRIBUTION}</p>
      <div className="btn-row">
        <a className="btn" href={phetPage(sim.slug)} target="_blank" rel="noopener noreferrer">{t('sims.openNewTab')} ↗</a>
      </div>
      <p className="muted small">{t('sims.licence')}</p>
    </div>
  );
}

/** Small block shown under each ChemAI lab simulation. */
export function RelatedPhet({ slugs }: { slugs: string[] }) {
  const { t, lang } = useI18n();
  if (!PHET_ENABLED || slugs.length === 0) return null;
  return (
    <section className="panel related">
      <h3 className="h-small">{t('sims.related')}</h3>
      <div className="examples">
        {slugs.map((slug) => {
          const s = PHET_SIMS.find((x) => x.slug === slug);
          return s ? <Link key={slug} to={`/simulations/phet/${slug}`} className="chip-btn">{phetTitle(s, lang)} · PhET</Link> : null;
        })}
      </div>
    </section>
  );
}
